import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  BasicDialogService,
  NgDialogModule,
} from '@principle-theorem/ng-shared';
import { ACC_SUCCESSFUL_TEST_SCENARIO } from '@principle-theorem/principle-core';
import {
  IAccClaimFundingRequest,
  IPatient,
  IPractice,
  AccPatientFormType,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef } from '@principle-theorem/shared';
import { AccClaimLodgementFormComponent } from '../acc-claim-lodgement-form/acc-claim-lodgement-form.component';

export interface IAccClaimLodgementFormDialogData {
  practice: WithRef<IPractice>;
  patient: WithRef<IPatient>;
  testMode: boolean;
  claim: WithRef<IAccClaimFundingRequest>;
  accPatientFormType?: AccPatientFormType;
}

@Component({
  selector: 'pr-acc-claim-lodgement-form-dialog',
  imports: [NgDialogModule, AccClaimLodgementFormComponent],
  templateUrl: './acc-claim-lodgement-form-dialog.component.html',
  styleUrls: ['./acc-claim-lodgement-form-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccClaimLodgementFormDialogComponent {
  private _basicDialog = inject(BasicDialogService);
  data = inject<IAccClaimLodgementFormDialogData>(MAT_DIALOG_DATA);
  dialogRef =
    inject<
      MatDialogRef<
        AccClaimLodgementFormComponent,
        WithRef<IAccClaimFundingRequest>
      >
    >(MatDialogRef);

  @ViewChild(AccClaimLodgementFormComponent)
  formComponent?: AccClaimLodgementFormComponent;

  constructor() {
    this.dialogRef.disableClose = true;

    this.dialogRef
      .keydownEvents()
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (event.key === 'Escape') {
          void this.onCloseRequested();
        }
      });

    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        void this.onCloseRequested();
      });
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: BeforeUnloadEvent): void {
    if (this.formComponent && this.formComponent.hasUnsavedChanges()) {
      event.preventDefault();
      event.returnValue =
        'You have unsaved changes. Are you sure you want to leave?';
    }
  }

  onSubmit(claim: WithRef<IAccClaimFundingRequest>): void {
    this.dialogRef.close(claim);
  }

  async onCloseRequested(): Promise<void> {
    if (this.formComponent && this.formComponent.hasUnsavedChanges()) {
      const confirmed = await this._basicDialog.confirm({
        title: 'Unsaved Changes',
        prompt: 'You have unsaved changes. Do you want to save before closing?',
        submitLabel: 'Save & Close',
      });

      if (confirmed) {
        await this.formComponent.saveProgress();
        return this.dialogRef.close();
      }
    }

    this.dialogRef.close();
  }

  submitTestClaim(): void {
    if (!this.data.testMode) {
      return;
    }

    this.dialogRef.close({
      ...this.data.claim,
      data: {
        claimData: ACC_SUCCESSFUL_TEST_SCENARIO(
          this.data.practice,
          this.data.patient
        ),
        metadata: {},
      },
    });
  }
}
